/**
 * AssetPromotions — Queue of collection records suggested for asset promotion.
 */

import { useState, useEffect, useCallback } from "react";
import { Table, Button, Tag, Space, Modal, Input, Select, message } from "antd";
import {
	RocketOutlined,
	CloseCircleOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import {
	getPromotionSuggestions,
	promoteToAsset,
	dismissPromotion,
	type CollectionRecord,
} from "@/services/data-collection-service";
import "./AssetPromotions.scss";

const AssetPromotions = () => {
	const [records, setRecords] = useState<CollectionRecord[]>([]);
	const [loading, setLoading] = useState(true);
	const [promoteModal, setPromoteModal] = useState<CollectionRecord | null>(null);
	const [assetTag, setAssetTag] = useState("");
	const [promoting, setPromoting] = useState(false);

	const fetch = useCallback(async () => {
		setLoading(true);
		try {
			const data = await getPromotionSuggestions();
			setRecords(data);
		} catch { /* ignore */ }
		setLoading(false);
	}, []);

	useEffect(() => { fetch(); }, [fetch]);

	const handlePromote = async () => {
		if (!promoteModal) return;
		setPromoting(true);
		try {
			await promoteToAsset(promoteModal.id, { assetTag: assetTag || undefined });
			message.success("Record promoted to asset!");
			setPromoteModal(null);
			setAssetTag("");
			fetch();
		} catch {
			message.error("Promotion failed");
		}
		setPromoting(false);
	};

	const handleDismiss = async (id: number) => {
		try {
			await dismissPromotion(id);
			message.success("Suggestion dismissed");
			fetch();
		} catch {
			message.error("Failed to dismiss");
		}
	};

	const columns = [
		{
			title: "Template",
			key: "template",
			render: (_: unknown, r: CollectionRecord) => (
				<div>
					<div style={{ fontWeight: 500 }}>{r.templateName || "—"}</div>
					{r.templateCategory && <Tag color="purple" style={{ fontSize: 10 }}>{r.templateCategory}</Tag>}
				</div>
			),
		},
		{
			title: "Location",
			dataIndex: "locationName",
			key: "location",
		},
		{
			title: "Area",
			dataIndex: "areaName",
			key: "area",
			render: (v: string) => v || "—",
		},
		{
			title: "Data Preview",
			key: "data",
			render: (_: unknown, r: CollectionRecord) => {
				const data = r.finalData ?? r.aiExtractedData ?? {};
				const entries = Object.entries(data).slice(0, 3);
				return (
					<div style={{ fontSize: 12 }}>
						{entries.map(([k, v]) => (
							<div key={k}>
								<strong>{k.replace(/_/g, " ")}:</strong>{" "}
								{typeof v === "object" && v !== null && "value" in (v as any)
									? String((v as any).value)
									: String(v)}
							</div>
						))}
						{Object.keys(data).length > 3 && (
							<span style={{ color: "#8888a0" }}>
								+{Object.keys(data).length - 3} more
							</span>
						)}
					</div>
				);
			},
		},
		{
			title: "Date",
			dataIndex: "createdAt",
			key: "date",
			render: (v: string) => new Date(v).toLocaleDateString(),
		},
		{
			title: "Actions",
			key: "actions",
			render: (_: unknown, r: CollectionRecord) => (
				<Space>
					<Button
						type="primary"
						size="small"
						icon={<RocketOutlined />}
						onClick={() => setPromoteModal(r)}
					>
						Promote
					</Button>
					<Button
						size="small"
						icon={<CloseCircleOutlined />}
						onClick={() => handleDismiss(r.id)}
					>
						Dismiss
					</Button>
				</Space>
			),
		},
	];

	return (
		<div className="asset-promotions-page">
			<div className="ap-header">
				<h2>Asset Promotion Suggestions</h2>
				<Button icon={<ReloadOutlined />} onClick={fetch}>Refresh</Button>
			</div>

			<p className="ap-description">
				These collection records have been flagged by AI as potentially trackable assets.
				Review and promote them to create asset records, or dismiss the suggestion.
			</p>

			<Table
				dataSource={records}
				columns={columns}
				rowKey="id"
				loading={loading}
				size="small"
				pagination={false}
				locale={{ emptyText: "No promotion suggestions" }}
			/>

			{/* Promote Modal */}
			<Modal
				open={!!promoteModal}
				onCancel={() => { setPromoteModal(null); setAssetTag(""); }}
				onOk={handlePromote}
				confirmLoading={promoting}
				title="Promote to Asset"
				okText="Create Asset"
			>
				<p>This will create a new asset from collection record data.</p>
				<div style={{ marginTop: 12 }}>
					<label style={{ display: "block", marginBottom: 4, fontWeight: 500 }}>Asset Tag (optional)</label>
					<Input
						placeholder="e.g., CARPET-001"
						value={assetTag}
						onChange={(e) => setAssetTag(e.target.value)}
					/>
				</div>
			</Modal>
		</div>
	);
};

export default AssetPromotions;
